import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import { useForm } from "../useForm";
import { useEffect, useState } from "react";
import { labSampleStatusOptions } from "../../../shared/utility";
import ButtonSpinner from "../../controls/ButtonSpinner";
import MediaUi from "../../importMedia/MediaUi";
import DatePickerInput from "../../controls/DatePickerInput";
import { useNavigate } from "react-router-dom";

const initialFValues = {
  lab_id: "",
  lot_number: "",
  product_name: "",
  plu: "",
  manufactured_product_id: "",
  barcode: "",
  weight_in_kg: "",
  weight_in_lbs: "",
  piece_count: "",
  notes: "",
  lab_date: null,
  completed_date: null,
  status: { value: -1, label: "" },
  laboratory: {},
  uploaded_media_1: null,
  uploaded_media_2: null,
  uploaded_media_3: null,
  created_by_name: "",
};

export default function LabSampleForm(props) {
  const {
    inProgress,
    labs,
    labSample,
    isEdit,
    createLabSample,
    updateLabSample,
  } = props;

  const navigate = useNavigate();

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if (!isEdit) {
      if ("lab_id" in fieldValues) {
        temp.lab_id =
          fieldValues.lab_id && fieldValues.lab_id !== -1
            ? null
            : "Laboratory is required";
      }
      if ("lot_number" in fieldValues) {
        temp.lot_number = fieldValues.lot_number
          ? null
          : "Lot number is required";
      }
      if ("product_name" in fieldValues) {
        temp.product_name = fieldValues.product_name
          ? null
          : "Product name is required";
      }
      if ("plu" in fieldValues) {
        temp.plu = fieldValues.plu ? null : "PLU is required";
      }
      if ("weight_in_kg" in fieldValues) {
        if (fieldValues.weight_in_kg) {
          temp.weight_in_kg = !isNaN(Number(fieldValues.weight_in_kg))
            ? null
            : "Amount in kg must be a number";
        } else {
          temp.weight_in_kg = "Amount in kg is required";
        }
      }
      if ("weight_in_lbs" in fieldValues) {
        if (fieldValues.weight_in_lbs) {
          temp.weight_in_lbs = !isNaN(Number(fieldValues.weight_in_lbs))
            ? null
            : "Amount in lbs must be a number";
        } else {
          temp.weight_in_lbs = "Amount in lbs is required";
        }
      }
      if ("piece_count" in fieldValues) {
        if (fieldValues.piece_count) {
          temp.piece_count = Number.isInteger(Number(fieldValues.piece_count))
            ? null
            : "Piece count must be an integer";
        } else {
          temp.piece_count = "Piece count is required";
        }
      }
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === null);
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    true,
    validate,
  );

  useEffect(() => {
    if (isEdit && labSample) {
      const statusSelected = labSampleStatusOptions.find(
        (el) => el.value === labSample.status,
      );

      setValues({
        ...labSample,
        status: statusSelected || { value: -1, label: "" },
        laboratory: labSample.laboratory
          ? { value: labSample.laboratory.id, label: labSample.laboratory.name }
          : {},
      });
    }
  }, [labSample, isEdit]);

  const [media1, setMedia1] = useState(null);
  const [media2, setMedia2] = useState(null);
  const [media3, setMedia3] = useState(null);

  // Handle SelectMenu changes (react-select returns objects)
  const handleSelectEvent = (e) => {
    const selected = e?.target?.value;
    const name = e?.target?.name;

    if (name === "laboratory") {
      const lab_id =
        selected && typeof selected === "object" ? selected.value : selected;
      setValues({ ...values, lab_id, laboratory: selected });
    } else {
      handleInputChange(e);
    }
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault && e.preventDefault();

    if (!validate()) {
      return;
    }

    if (isEdit) {
      await updateLabSample(values);
    } else {
      const files = { media1, media2, media3 };
      const result = await createLabSample(values, files);
      if (result.success) {
        navigate("/lab-samples");
      } else if (result.errors) {
        // Map server-side errors to form fields
        setErrors(result.errors);
      }
    }
  };

  return (
    <div className="justify-between">
      <form
        onSubmit={handleSubmit}
        className="pt-6 pb-5 grid grid-cols-1 gap-2"
      >
        <div className="p-4 card-custom space-y-5">
          <div className="grid sm:grid-cols-4 gap-4">
            <SelectMenu
              handleInputChange={handleSelectEvent}
              name="laboratory"
              options={labs}
              value={
                isEdit
                  ? values.laboratory
                  : labs.find((o) => o.value === values.lab_id) || null
              }
              error={errors.lab_id || errors.laboratory}
              title={"Laboratory"}
              showLabel={true}
              disabled={isEdit && values.status.value > 0}
            />

            <DatePickerInput
              label="Lab submission date"
              name="lab_date"
              onChange={handleInputChange}
              value={values.lab_date}
              labelOn={true}
              dateFormat="LLL dd, YYY"
              type="text"
              error={errors.lab_date}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            {isEdit && (
              <Input
                onChange={handleInputChange}
                label="Created by"
                name="created_by_name"
                value={values.created_by_name}
                labelOn={true}
                type="text"
                disabled={true}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
            )}

            {isEdit && (
              <SelectMenu
                handleInputChange={handleInputChange}
                name="status"
                options={labSampleStatusOptions}
                value={values.status}
                title={"Status"}
                error={errors.status}
                showLabel={true}
              />
            )}

            <Input
              onChange={handleInputChange}
              label="Lot number"
              name="lot_number"
              value={values.lot_number}
              error={errors.lot_number}
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="Product name"
              name="product_name"
              value={values.product_name}
              error={errors.product_name}
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="PLU"
              name="plu"
              value={values.plu}
              error={errors.plu}
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <DatePickerInput
              label="Lab Completed date"
              name="completed_date"
              onChange={handleInputChange}
              value={values.completed_date}
              labelOn={true}
              dateFormat="LLL dd, YYY"
              type="text"
              error={errors.completed_date}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <Input
              label="Manufactured Product ID"
              name="manufactured_product_id"
              onChange={handleInputChange}
              value={values.manufactured_product_id}
              error={errors.manufactured_product_id}
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="Barcode"
              name="barcode"
              value={values.barcode}
              error={errors.barcode}
              labelOn={true}
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="Amount in kg"
              name="weight_in_kg"
              value={values.weight_in_kg}
              error={errors.weight_in_kg}
              labelOn={true}
              type="number"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="Amount in lbs"
              name="weight_in_lbs"
              value={values.weight_in_lbs}
              error={errors.weight_in_lbs}
              labelOn={true}
              type="number"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label={isEdit ? "Amount in pieces" : "Piece count"}
              name="piece_count"
              value={values.piece_count}
              error={errors.piece_count}
              labelOn={true}
              type="number"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <Input
              onChange={handleInputChange}
              label="Notes"
              name="notes"
              value={values.notes}
              error={errors.notes}
              labelOn={true}
              type="text"
              parentClass="col-span-2"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
          </div>
        </div>

        <div className="px-2 py-4 sm:px-5 card-custom grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MediaUi
            title="Document 1"
            uploadedImage={media1}
            setUploadedImage={setMedia1}
            isEdit={isEdit}
            imageUrl={isEdit ? values.media_1_url : null}
            imageName="uploaded_media_1"
            handleInputChange={handleInputChange}
            onDelete={() => {
              if (isEdit) {
                setValues({
                  ...values,
                  uploaded_media_1: null,
                  media_1: "",
                  media_1_url: null,
                });
              }
              setMedia1(null);
            }}
          />

          <MediaUi
            title="Document 2"
            uploadedImage={media2}
            setUploadedImage={setMedia2}
            isEdit={isEdit}
            imageUrl={isEdit ? values.media_2_url : null}
            imageName="uploaded_media_2"
            handleInputChange={handleInputChange}
            onDelete={() => {
              if (isEdit) {
                setValues({
                  ...values,
                  uploaded_media_2: null,
                  media_2: "",
                  media_2_url: null,
                });
              }
              setMedia2(null);
            }}
          />

          <MediaUi
            title="Document 3"
            uploadedImage={media3}
            setUploadedImage={setMedia3}
            isEdit={isEdit}
            imageUrl={isEdit ? values.media_3_url : null}
            imageName="uploaded_media_3"
            handleInputChange={handleInputChange}
            onDelete={() => {
              if (isEdit) {
                setValues({
                  ...values,
                  uploaded_media_3: null,
                  media_3: "",
                  media_3_url: null,
                });
              }
              setMedia3(null);
            }}
          />
        </div>

        <div className="mt-6 flex justify-end">
          {!isEdit && (
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 mr-3"
              onClick={() => navigate("/lab-samples")}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            <ButtonSpinner
              spin={inProgress}
              text={isEdit ? "Save" : "Create"}
            />
          </button>
        </div>
      </form>
    </div>
  );
}
